import { UniqMap } from 'src/lib/UniqMap';
import {
  SUBSCRIPTION_PLAN_CATEGORY,
  SUBSCRIPTION_PLAN,
  SUBSCRIPTION_PLAN_ACTIVE,
  SUBSCRIPTION_PLAN_LEGACY,
  SUBSCRIPTION_DATA_DRIVEN_SEAT_TIER,
  SUBSCRIPTION_SEAT_TIER,
} from './const';

export const isSubscriptionPlanDeprecated = (plan) =>
  Object.values(SUBSCRIPTION_PLAN_LEGACY).includes(plan);

const isPlanFromCategory = (plan, planType) => getSubscriptionPlanCategory(plan) === planType;

export const isPlanFromBasicCategory = (plan) =>
  isPlanFromCategory(plan, SUBSCRIPTION_PLAN_CATEGORY.basic);
export const isPlanFromStarterCategory = (plan) =>
  isPlanFromCategory(plan, SUBSCRIPTION_PLAN_CATEGORY.starter);
export const isPlanFromDataDrivenCategory = (plan) =>
  isPlanFromCategory(plan, SUBSCRIPTION_PLAN_CATEGORY.dataDriven);
export const isPlanFromBusinessCategory = (plan) =>
  isPlanFromCategory(plan, SUBSCRIPTION_PLAN_CATEGORY.business);

// Enterprise and POC plans are considered as the same category
export const isPlanFromEnterpriseCategory = (plan) =>
  isPlanFromCategory(plan, SUBSCRIPTION_PLAN_CATEGORY.enterprise) ||
  isPlanFromCategory(plan, SUBSCRIPTION_PLAN_CATEGORY.poc);

export const isPocPlan = (plan) => plan === SUBSCRIPTION_PLAN.pocLegacy;

export const isPlanFromBasicOrStarterCategory = (plan) =>
  isPlanFromBasicCategory(plan) || isPlanFromStarterCategory(plan);
export const isPlanFromStarterOrDataDrivenCategory = (plan) =>
  isPlanFromStarterCategory(plan) || isPlanFromDataDrivenCategory(plan);
export const isPlanFromBusinessOrEnterpriseCategory = (plan) =>
  isPlanFromBusinessCategory(plan) || isPlanFromEnterpriseCategory(plan);
export const isSelfServicePlan = (plan) => !isPlanFromEnterpriseCategory(plan);
export const isPlanWithSeatTiers = (plan) =>
  isPlanFromDataDrivenCategory(plan) || isPlanFromBusinessCategory(plan);

export const planCanBeCancelled = (plan) =>
  isPlanFromStarterCategory(plan) ||
  isPlanFromDataDrivenCategory(plan) ||
  isPlanFromBusinessCategory(plan);
export const planCanBeUpgraded = (plan) => !isPlanFromEnterpriseCategory(plan);
export const shouldUpgradeToBusiness = (plan) =>
  isPlanFromStarterCategory(plan) ||
  isPlanFromBasicCategory(plan) ||
  isPlanFromDataDrivenCategory(plan);

const SUBSCRIPTION_PLAN_PROPS = new UniqMap();

/**
 * Active plans
 */

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_ACTIVE.basic, {
  category: SUBSCRIPTION_PLAN_CATEGORY.basic,
  label: 'Free',
  performancePredictionsLimit: 25,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_ACTIVE.starter, {
  category: SUBSCRIPTION_PLAN_CATEGORY.starter,
  label: 'Starter',
  description:
    'For solo marketers & freelancers who want on-brand content backed by Anyword’s performance data.',
  performancePredictionsLimit: 100,
  seats: 1,
  price: 49,
  priceAnnually: 468,
  priceAnnuallyPerMonth: 39,
  percentOfAnnualDiscount: 20,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_ACTIVE.dataDriven, {
  category: SUBSCRIPTION_PLAN_CATEGORY.dataDriven,
  label: 'Data-Driven',
  description:
    'For marketing teams who want on-brand content backed by Anyword’s performance data.',
  tiers: SUBSCRIPTION_DATA_DRIVEN_SEAT_TIER,
  performancePredictionsLimit: 175,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_ACTIVE.business, {
  category: SUBSCRIPTION_PLAN_CATEGORY.business,
  label: 'Business',
  description:
    'For marketing teams who want better performance across channels with AI trained on your best messaging & past campaigns.',
  tiers: SUBSCRIPTION_SEAT_TIER,
  performancePredictionsLimit: 250,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_ACTIVE.businessPlus, {
  category: SUBSCRIPTION_PLAN_CATEGORY.business,
  label: 'Business Plus',
  performancePredictionsLimit: 375,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_ACTIVE.businessMax, {
  category: SUBSCRIPTION_PLAN_CATEGORY.business,
  label: 'Business Max',
  performancePredictionsLimit: 500,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_ACTIVE.performance, {
  category: SUBSCRIPTION_PLAN_CATEGORY.enterprise,
  label: 'Enterprise',
  description:
    'For teams who want custom-built AI tailored to their brand, with enterprise-grade security, privacy, and control.',
  performancePredictionsLimit: 500,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_ACTIVE.performancePlus, {
  category: SUBSCRIPTION_PLAN_CATEGORY.enterprise,
  label: 'Enterprise Plus',
  performancePredictionsLimit: 1000,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_ACTIVE.performanceMax, {
  category: SUBSCRIPTION_PLAN_CATEGORY.enterprise,
  label: 'Enterprise Max',
  performancePredictionsLimit: 2000,
});

/**
 * Legacy plans
 */
SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_LEGACY.basicTieredLegacy, {
  category: SUBSCRIPTION_PLAN_CATEGORY.basic,
  label: 'Basic Tiered - legacy',
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_LEGACY.starterLegacy, {
  category: SUBSCRIPTION_PLAN_CATEGORY.starter,
  label: 'Starter - legacy',
  description:
    'For solo marketers & freelancers who want on-brand content backed by Anyword’s performance data.',
  seats: 1,
  price: 49,
  priceAnnually: 468,
  priceAnnuallyPerMonth: 39,
  percentOfAnnualDiscount: 20,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_LEGACY.ddTeamsLegacy, {
  category: SUBSCRIPTION_PLAN_CATEGORY.dataDriven,
  label: 'Data-Driven - legacy',
  description:
    'For marketing teams who want on-brand content backed by Anyword’s\nperformance data.',
  tiers: SUBSCRIPTION_DATA_DRIVEN_SEAT_TIER,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_LEGACY.businessSelfServeLegacy, {
  category: SUBSCRIPTION_PLAN_CATEGORY.business,
  label: 'Business - legacy',
  description:
    'For marketing teams who want better performance across channels with AI trained on your best messaging & past campaigns.',
  tiers: SUBSCRIPTION_SEAT_TIER,
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_LEGACY.proPublisherLegacy, {
  category: SUBSCRIPTION_PLAN_CATEGORY.business,
  label: 'ProPublisher - legacy',
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_LEGACY.pocLegacy, {
  category: SUBSCRIPTION_PLAN_CATEGORY.poc,
  label: 'Proof of Concept - legacy',
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_LEGACY.businessLegacy, {
  category: SUBSCRIPTION_PLAN_CATEGORY.enterprise,
  label: 'Enterprise',
  description:
    'For teams who want custom-built AI tailored to their brand, with enterprise-grade security, privacy, and control.',
});

SUBSCRIPTION_PLAN_PROPS.add(SUBSCRIPTION_PLAN_LEGACY.enterpriseLegacy, {
  category: SUBSCRIPTION_PLAN_CATEGORY.enterprise,
  label: 'Enterprise',
});

export const getPlanProperty = (plan, property, defaultValue = undefined) => {
  const props = SUBSCRIPTION_PLAN_PROPS.get(plan);
  return (props && props[property]) || defaultValue;
};

export const getSubscriptionPlanLabel = (plan) => getPlanProperty(plan, 'label', plan || '');

export const getSubscriptionPlanCategory = (plan) => getPlanProperty(plan, 'category');

export const getSubscriptionPlanSubtitle = (plan) => getPlanProperty(plan, 'subtitle', '');

export const getSubscriptionPlanDescription = (plan) => getPlanProperty(plan, 'description', '');

export const getSubscriptionPlanWordsLimit = (plan) => getPlanProperty(plan, 'wordsLimit', 0);

export const getSubscriptionPlanTiers = (plan, defaultValue) =>
  getPlanProperty(plan, 'tiers', defaultValue);

export const getPlanPerformancePredictionsLimit = (plan) =>
  getPlanProperty(plan, 'performancePredictionsLimit', 0);
